
import { observer } from "mobx-react";
import {
  Typography,
  useTheme,
  IconButton,
  Grid,
  Box,
  TextField
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MoreVert } from '@material-ui/icons';
import { DataGrid, GridColumnProps, Button, StandardDialog, SelectMode, SelectSide } from '@roc/ui';
import { useEffect, useMemo, useState } from "react";
import { AddContactsToCadenceModal } from "./marketing/addContactsToCadenceModal";
import { useStore } from '@roc/client-portal-shared/hooks';
import { businessUnitOptions } from "../leadsDashboard/allLeadDashboard";
import { isLeadDashboard } from "../leads/listViews/leadsDashboardGrid";
import { LEAD_OBJECT_TYPE } from "../leads/constants/constants";
import { SelectCadenceListStepper } from "./selectCadenceListStepper";
import { insertIf } from "@roc/feature-utils";
import { Autocomplete } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: '0px',
    height: "400px"
  },
  peopleContainer: {
    minHeight: "400px",
    maxHeight: "500px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "10px",
    padding: "10px",
    backgroundColor: "white",
    borderRadius: "8px",
    marginTop: theme.spacing(5),
  },
  noStepsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    marginTop: theme.spacing(10),
  },
  associateButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  buttonContainer: {
    marginTop: theme.spacing(5),
  },
  a: {
    height: "400px",
  },
}));

export const SelectCustomProspectsDashboard = observer(({ listViewId, gritStore, isStandard, handleCloseModal }) => {
  const { cadenceStore, globalStore, salesforceLeadStore } = useStore();
  const [column, setColumn] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState<string[]>([]);
  const classes = useStyles();

  useEffect(() => {
    if (isStandard != undefined) {
      cadenceStore.setStandartList(isStandard)
    }
    setColumns()
    if (listViewId != null) {
      gritStore.fetchGridData();
    }
  }, [listViewId, isStandard])

  useEffect(() => {
    const loadOwners = async () => {
      if (!salesforceLeadStore.owners || salesforceLeadStore.owners.length === 0) {
        await salesforceLeadStore.getAllOwners();
      }
      const filteredOwner = (salesforceLeadStore.owners || []).filter(o => o.active && belongsToBU(o?.userName));
      const names = filteredOwner.map(o => `${o.firstName} ${o.lastName}`.trim());
      const distinct = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
      setOwnerOptions(distinct);
    };
    loadOwners();
  }, [salesforceLeadStore]);

  const belongsToBU = (email: string) => {
    const e = (email || '').toLowerCase();
    if (cadenceStore?.gridBusinessUnit === 'FACO') return e.includes('faco');
    if (cadenceStore?.gridBusinessUnit === 'CIVIC') return e.includes('civicfs');
    if (cadenceStore?.gridBusinessUnit === 'ROC') return e.includes('roc');
    return true;
  };

  const ownerField = useMemo(() => {
    const type = cadenceStore.currentDashboardId?.type?.toUpperCase();
    if (type === "BORROWER") {
      if (cadenceStore.gridBusinessUnit === "FACO") return "facoOwner";
      if (cadenceStore.gridBusinessUnit === "CIVIC") return "civicOwner";
      return "rocOwner";
    }
    if (type === "LEAD") return "ownerName";
    return "primaryRm";
  }, [cadenceStore.currentDashboardId?.type, cadenceStore.gridBusinessUnit]);

  const selectedOwner = gritStore?.filterModel?.[ownerField]?.filter ?? "";

  const setOwnerFilter = (value: string) => {
    const next = { ...(gritStore.filterModel || {}) };
    if (!value) delete next[ownerField];
    else {
      next[ownerField] = { filterType: "text", type: "equals", filter: value };
    }
    gritStore.setFilterModel(next);
  };

  const handleSubmissionEntireList = () => {
    cadenceStore.saveListViewId(listViewId, false)
    handleCloseModal()
  }

  const handleSubmissionPartialList = () => {
    cadenceStore.saveListViewId(listViewId, true)
    handleCloseModal()
  }

  const handleAddNewProspects = () => {
    cadenceStore.addNewProspects()
    handleCloseModal()
  }

  const onRowSelected = params => {
    if (params.node.isSelected()) {
      const lead = params.node.data
      if (lead.leadId) {
        cadenceStore.addIdToSelectedLeadList(lead.leadId);
      } else if (lead.contactId) {
        cadenceStore.addIdToSelectedLeadList(lead.contactId);
      }
    } else {
      const lead = params.node.data
      if (lead.leadId) {
        cadenceStore.removeIdFromSelectedLeadList(lead.leadId);
      } else if (lead.contactId) {
        cadenceStore.removeIdFromSelectedLeadList(lead.contactId);
      }
    }
  };

  const setColumns = () => {
    const leadColumns: GridColumnProps[] = [
      { field: 'name', headerName: 'Name', minWidth: 120 },
      { field: 'email', headerName: 'Email', minWidth: 120 },
      { field: 'phone', headerName: 'Phone Number', minWidth: 120 },
      { field: 'ownerName', headerName: 'Owner Name', minWidth: 120 },
      {
        field: 'journeyBuilderStatus',
        headerName: 'Journey Status',
        minWidth: 120,
        valueFormatter: (params) => {
          return params.value != null ? params.value : "Not in Journey";
        }
      },
    ];

    const contactColumns: GridColumnProps[] = [
      { field: 'name', headerName: 'Name', minWidth: 120 },
      { field: 'email', headerName: 'Email', minWidth: 120 },
      { field: 'phone', headerName: 'Phone Number', minWidth: 120 },
      { field: 'primaryRm', headerName: 'Owner Name', minWidth: 120 },
      {
        field: 'journeyBuilderStatus',
        headerName: 'Journey Status',
        minWidth: 120,
        valueFormatter: (params) => {
          return params.value != null ? params.value : "Not in Journey";
        }
      },
    ];

    const borrowerColumns: GridColumnProps[] = [
      ...insertIf(cadenceStore.standartList === true, [{
        field: 'fullName', headerName: 'Name', minWidth: 120
      }]),
      ...insertIf(!cadenceStore.standartList, [{
        field: 'name', headerName: 'Name', minWidth: 120
      }]),
      { field: 'email', headerName: 'Email', minWidth: 120 },
      { field: 'phone', headerName: 'Phone Number', minWidth: 120 },
      ...insertIf(cadenceStore.gridBusinessUnit === "FACO", [{
        field: 'facoOwner', headerName: 'Owner Name', minWidth: 120
      }]),
      ...insertIf(cadenceStore.gridBusinessUnit === "CIVIC", [{
        field: 'civicOwner', headerName: 'Owner Name', minWidth: 120
      }]),
      ...insertIf(cadenceStore.gridBusinessUnit === "ROC", [{
        field: 'rocOwner', headerName: 'Owner Name', minWidth: 120
      }]),
      {
        field: 'journeyBuilderStatus',
        headerName: 'Journey Status',
        minWidth: 120,
        valueFormatter: (params) => {
          return params.value != null ? params.value : "Not in Journey";
        }
      },
    ];

    if (cadenceStore.currentDashboardId?.type?.toUpperCase() === "LEAD") {
      setColumn(leadColumns)
    } else if (cadenceStore.currentDashboardId?.type?.toUpperCase() === "BORROWER") {
      setColumn(borrowerColumns)
    } else {
      setColumn(contactColumns)
    }

  }

  const openMenu = (event, record) => {
    console.log('Open menu:', record);
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    gritStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    gritStore.setFilterModel(filterModel);
  };

  const handleOnBack = () => {
    cadenceStore.resetSelectedLeadList();
  };

  const frameworkComponents = {
    actionCellRenderer: (params) => (
      <IconButton onClick={(e) => openMenu(e, params.node.data)}>
        <MoreVert color="primary" />
      </IconButton>
    ),
  };

  const OwnerFilterToolbar = ({ }) => (
    <Box mb={2} px={1} py={1.5} display="flex" alignItems="center" justifyContent="space-between" bgcolor="white" borderRadius={1}>
      <Grid item container>
        <Grid item xs={12}>
          <Typography variant="subtitle1">Owner</Typography>
        </Grid>
        <Grid item xs={4}>
          <Autocomplete
            id="owner-filter-autocomplete"
            options={ownerOptions}
            value={selectedOwner || null}
            onChange={(event, newValue) => {
              setOwnerFilter(newValue || "");
            }}
            clearOnEscape
            size="small"
            style={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Select owner"
              />
            )}
            getOptionLabel={(option) => option ?? ""}
          />
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      <Grid item container>
        <Grid item xs={12}>
          <DataGrid
            toolbar={
              <OwnerFilterToolbar />
            }
            selectMode={SelectMode.MULTIPLE}
            selectSide={SelectSide.LEFT}
            key={`testingid`}
            className={classes.container}
            headerCheckboxSelection={true}
            suppressRowClickSelection={true}
            frameworkComponents={frameworkComponents}
            columns={column}
            rows={gritStore.gridData.data.rows}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            rowHeight={45}
            checkBoxColumnPinned={true}
            enablePaginationSelectPage={true}
            showPagination={true}
            isLoading={gritStore.isLoading}
            showFilters={gritStore.showFilters}
            sortModel={gritStore.sortModel}
            filterModel={gritStore.filterModel}
            paginationData={gritStore.gridData.meta}
            setPageNumber={gritStore.setPageNumber}
            onRowSelected={onRowSelected}
            getRowNodeId={(data) => data.leadId ? data.leadId : data.contactId}
          />
        </Grid>
        <Grid container spacing={2} justifyContent="flex-end" className={classes.buttonContainer}>
          <Grid item xs={2}>
            <Button
              onClick={handleOnBack}
              color="primary"
              variant="outlined"
              testId="cancel"
              fullWidth
            >
              Back
            </Button>
          </Grid>
          {
            cadenceStore.isAddingMoreProspectsActive ?
              <>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddNewProspects}
                    testId="next"
                    disabled={gritStore.isLoading}
                    fullWidth
                  >
                    Add New Prospects
                  </Button>
                </Grid>
              </>
              :
              <>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmissionEntireList}
                    testId="next"
                    disabled={gritStore.isLoading}
                    fullWidth
                  >
                    Associate Entire List
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmissionPartialList}
                    testId="next"
                    fullWidth
                    disabled={gritStore.isLoading}
                  >
                    Associate Partial List
                  </Button>
                </Grid>
              </>
          }
        </Grid>
      </Grid>
    </>
  );
});
